/**
 * Drag controller — handles pointerdown on `.cm-magic-drag-handle` markers,
 * tracks pointermove to highlight legal drop targets, and on pointerup
 * commits an atomic two-change transaction that moves the source MovableRange
 * to the chosen target.
 *
 * We deliberately avoid HTML5 DnD because it conflicts with CodeMirror's
 * own selection / dropCursor handling. All pointer events are managed by
 * a single ViewPlugin attached to the EditorView.
 *
 * Fold restoration: CM6 fold state is anchored to old-state ranges; when we
 * move text, the old foldEffects no longer point at the moved block. We
 * snapshot fold state before the move (via `getFoldSnapshot`), dispatch the
 * move, then dispatch fold effects in a follow-up frame using the new state.
 */

import { foldEffect } from "@codemirror/language";
import {
    Annotation,
    type ChangeSpec,
    type EditorState,
    type Extension,
} from "@codemirror/state";
import { EditorView, ViewPlugin } from "@codemirror/view";

import {
    DRAG_HANDLE_ATTR,
    DRAG_HANDLE_CSS_CLASS,
} from "./drag-handle-gutter";
import { buildFoldEffects, getFoldSnapshot } from "./fold";
import {
    type DropTarget,
    type MovableRange,
    getDropTargetsFor,
    getMovableRanges,
} from "./movable-ranges";

/** Annotation marking the move transaction (so other plugins can identify it). */
export const magicDragMoveAnnotation = Annotation.define<true>();

const DROP_INDICATOR_CLASS = "cm-magic-drop-indicator";

interface DragSession {
    sourceId: string;
    sourceRange: MovableRange;
    targets: DropTarget[];
    /** Currently hovered target (if any) */
    activeTarget: DropTarget | null;
    /** DOM element for the drop indicator line */
    indicator: HTMLElement | null;
}

class MagicDragController {
    private session: DragSession | null = null;
    private readonly onPointerMove = (e: PointerEvent) => this.handleMove(e);
    private readonly onPointerUp = (e: PointerEvent) => this.handleUp(e);
    private readonly onPointerDown = (e: PointerEvent) => this.handleDown(e);

    constructor(private readonly view: EditorView) {
        view.scrollDOM.addEventListener("pointerdown", this.onPointerDown, true);
    }

    destroy(): void {
        this.view.scrollDOM.removeEventListener(
            "pointerdown",
            this.onPointerDown,
            true,
        );
        this.endSession();
    }

    private handleDown(event: PointerEvent): void {
        if (event.button !== 0) return;
        const target = event.target as HTMLElement | null;
        const handle = target?.closest(
            `.${DRAG_HANDLE_CSS_CLASS}`,
        ) as HTMLElement | null;
        if (!handle) return;

        const id = handle.getAttribute(DRAG_HANDLE_ATTR);
        if (!id) return;

        const ranges = getMovableRanges(this.view.state);
        const sourceRange = ranges.find((r) => r.id === id);
        if (!sourceRange) return;

        const targets = getDropTargetsFor(this.view.state, ranges, id);

        // Even if there are no legal drop targets we still consume the event
        // (so the editor doesn't try to start a text selection from the gutter).
        event.preventDefault();
        event.stopPropagation();

        this.session = {
            sourceId: id,
            sourceRange,
            targets,
            activeTarget: null,
            indicator: this.createIndicator(),
        };

        window.addEventListener("pointermove", this.onPointerMove, true);
        window.addEventListener("pointerup", this.onPointerUp, true);
        document.body.classList.add("cm-magic-dragging");
    }

    private handleMove(event: PointerEvent): void {
        if (!this.session) return;
        const target = this.findClosestTarget(event.clientY);
        this.session.activeTarget = target;
        this.updateIndicator(target);
    }

    private handleUp(event: PointerEvent): void {
        if (!this.session) return;
        const session = this.session;
        this.endSession();

        const target = session.activeTarget;
        if (!target) return;

        // Re-resolve the source range against latest state in case the doc
        // changed during the drag (defensive).
        const ranges = getMovableRanges(this.view.state);
        const source = ranges.find((r) => r.id === session.sourceId);
        if (!source) return;

        // Skip no-op (target inside source range)
        if (target.pos >= source.fromPos && target.pos <= source.toPos + 1) {
            return;
        }

        this.commitMove(source, target);
        event.preventDefault();
    }

    private endSession(): void {
        window.removeEventListener("pointermove", this.onPointerMove, true);
        window.removeEventListener("pointerup", this.onPointerUp, true);
        document.body.classList.remove("cm-magic-dragging");
        if (this.session?.indicator) {
            this.session.indicator.remove();
        }
        this.session = null;
    }

    private createIndicator(): HTMLElement {
        const el = document.createElement("div");
        el.className = DROP_INDICATOR_CLASS;
        el.style.position = "absolute";
        el.style.left = "0";
        el.style.right = "0";
        el.style.height = "2px";
        el.style.pointerEvents = "none";
        el.style.zIndex = "5";
        el.style.display = "none";
        this.view.scrollDOM.appendChild(el);
        return el;
    }

    private findClosestTarget(clientY: number): DropTarget | null {
        if (!this.session) return null;
        const { targets } = this.session;
        if (targets.length === 0) return null;

        let best: DropTarget | null = null;
        let bestDist = Infinity;
        for (const t of targets) {
            const coords = this.view.coordsAtPos(t.pos);
            if (!coords) continue;
            const targetY = coords.top;
            const dist = Math.abs(targetY - clientY);
            if (dist < bestDist) {
                bestDist = dist;
                best = t;
            }
        }
        // Threshold: only show indicator if the cursor is reasonably close
        // (within ~half a viewport height) — avoids picking distant targets.
        if (bestDist > this.view.scrollDOM.clientHeight) return null;
        return best;
    }

    private updateIndicator(target: DropTarget | null): void {
        if (!this.session?.indicator) return;
        const indicator = this.session.indicator;
        if (!target) {
            indicator.style.display = "none";
            return;
        }
        const coords = this.view.coordsAtPos(target.pos);
        if (!coords) {
            indicator.style.display = "none";
            return;
        }
        const containerRect = this.view.scrollDOM.getBoundingClientRect();
        const top =
            coords.top - containerRect.top + this.view.scrollDOM.scrollTop;
        indicator.style.top = `${top}px`;
        indicator.style.display = "block";
    }

    /**
     * Commit the move as a two-change transaction. CM6 sorts and remaps
     * the changes itself, so we can pass deletion + insertion in a single
     * `state.changes()` call without manual offset math.
     *
     * Fold restoration runs in a follow-up dispatch (effects can't reference
     * old-state ranges in the same transaction).
     */
    private commitMove(source: MovableRange, target: DropTarget): void {
        const view = this.view;
        const state = view.state;
        const doc = state.doc;

        const srcFrom = source.fromPos;
        // toPos = end of last line; we want to also remove the trailing newline
        // (so the block doesn't leave a blank line behind) IF there is one.
        let srcTo = source.toPos;
        if (srcTo < doc.length && doc.sliceString(srcTo, srcTo + 1) === "\n") {
            srcTo += 1;
        }
        const srcText = doc.sliceString(srcFrom, srcTo);

        let dstPos = target.pos;
        // Clamp dstPos: if it's somehow inside the source range, abort.
        if (dstPos > srcFrom && dstPos < srcTo) return;

        const insertText = srcText.endsWith("\n") ? srcText : srcText + "\n";

        // Snapshot folds before the move so we can restore them after.
        const foldSnapshot = getFoldSnapshot(view);

        const changes: ChangeSpec[] = [
            { from: srcFrom, to: srcTo, insert: "" },
            { from: dstPos, insert: insertText },
        ];

        view.dispatch({
            changes,
            annotations: magicDragMoveAnnotation.of(true),
            userEvent: "magic.drag-block",
        });

        // Restore folds in a follow-up frame using the new state.
        requestAnimationFrame(() => {
            const newState: EditorState = view.state;
            const effects = buildFoldEffects(
                { state: newState },
                foldSnapshot,
            );
            // Filter to fold effects only (avoid double-applying anything else).
            const foldOnly = effects.filter((e) => e.is(foldEffect));
            if (foldOnly.length > 0) {
                view.dispatch({ effects: foldOnly });
            }
        });
    }
}

export function magicDragController(): Extension {
    return ViewPlugin.fromClass(MagicDragController);
}

export const magicDragTheme = EditorView.theme({
    // Visual style of `.cm-magic-drag-handle` is owned by the bookmarks gutter
    // (codemirror-bookmarks/index.ts). This theme only contributes the drop
    // indicator and global drag-state cursor overrides.
    ".cm-magic-drop-indicator": {
        backgroundColor: "#3b82f6",
        boxShadow: "0 0 4px rgba(59, 130, 246, 0.6)",
    },
    "body.cm-magic-dragging *": {
        cursor: "grabbing !important",
        userSelect: "none",
    },
    "body.cm-magic-dragging .cm-magic-drag-handle": {
        opacity: "1 !important",
    },
});
